import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import {
  Box,
  Grid,
  Card,
  Container,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  InputAdornment,
  Snackbar,
  Alert,
} from "@mui/material";
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import CloseIcon from '@mui/icons-material/Close';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import SearchIcon from '@mui/icons-material/Search';
import WarehouseOutlinedIcon from '@mui/icons-material/WarehouseOutlined';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import ExportExcel from '../../components/setting/ExportExcel';
import MonitorNavigation from '../../components/monitor/MonitorNavigation';
import LocationSelect from '../../components/register/LocationSelect';
import ProductIdentification from '../../components/register/ProductIdentification';
import WIPDetail from '../../components/register/WIPDetail';
import ProductionInputs from '../../components/register/ProductionInputs';
import SaveBar from '../../components/register/SaveBar';
import { useSelector } from 'react-redux';
import jwtDecode from 'jwt-decode';
import { useParams } from 'react-router-dom';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Checkbox from '@mui/material/Checkbox';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { printWipSticker } from '../../utils/printWipSticker';
import useCheckToken from '../../hooks/useCheckToken';
import useExchangeToken from '../../hooks/useExchangeToken';
import {
  createWipRegistration,
  deleteWipRegistration,
  getWipMaster,
  getWipMasterOptions,
  getWipRegistrations,
} from '../../services/wipTrackingApi';
import {
  addWipDuration,
  extractApiList,
  formatApiTime,
  mapRegistrationToRow,
  uniqueNonEmpty,
} from '../../utils/wipTracking';

const pad2 = (value) => String(value).padStart(2, '0');
const formatStickerDate = (value) => (
  `${pad2(value.getDate())}/${pad2(value.getMonth() + 1)}/${String(value.getFullYear()).slice(-2)}`
);
const formatLocalDate = (value) => (
  `${value.getFullYear()}-${pad2(value.getMonth() + 1)}-${pad2(value.getDate())}`
);
const formatLocalDateTime = (value) => (
  `${formatLocalDate(value)} ${pad2(value.getHours())}:${pad2(value.getMinutes())}:${pad2(value.getSeconds())}`
);
const calculateDelayMinutes = (start, end) => {
  if (!(start instanceof Date) || !(end instanceof Date)) return '';
  const minutes = Math.round((end.getTime() - start.getTime()) / 60000);
  return minutes >= 0 ? `${minutes} min` : '';
};
const dateAtTime = (baseDate, timeValue) => {
  const value = new Date(baseDate);
  const [hours, minutes, seconds] = String(timeValue || '').split(':').map(Number);
  if (Number.isNaN(value.getTime()) || !Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  value.setHours(hours, minutes, Number.isFinite(seconds) ? seconds : 0, 0);
  return value;
};
const isMesAccessToken = (token) => {
  try {
    const claims = jwtDecode(token);
    return claims?.token_type === 'access' && Number(claims?.exp) > Date.now() / 1000 + 60;
  } catch {
    return false;
  }
};

export default function Register() {
  useCheckToken();
  const exchangeToken = useExchangeToken();
  const { plant: routePlant = '', factory: routeFactory = '' } = useParams();

  const portalAccessToken = useSelector((state) => state.auth?.accessToken || '');
  const authAccessToken = useSelector((state) => state.auth?.access_token || '');
  const authPlantId = useSelector((state) => state.auth?.plant_id || '');
  const authFactory = useSelector((state) => state.auth?.factory || '');
  const loggedInName = useSelector((state) => state.auth?.display || state.auth?.email || '');
  const plantId = routePlant || authPlantId;
  const factory = routeFactory || authFactory;

  const [location, setLocation] = useState("");
  const [productName, setProductName] = useState("");
  const [matCode, setMatCode] = useState("");
  const [productType, setProductType] = useState("");
  const [component, setComponent] = useState("");
  const [processStep, setProcessStep] = useState("");
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [lot, setLot] = useState("");
  const [batch, setBatch] = useState("");
  const [shift, setShift] = useState("D");
  const [weight, setWeight] = useState('');
  const [qty, setQty] = useState('');
  const [refValue, setRefValue] = useState('');
  const [noValue, setNoValue] = useState('');
  const [operator, setOperator] = useState('');
  const [searchText, setSearchText] = useState('');
  const [saveToastOpen, setSaveToastOpen] = useState(false);
  const [printErrorOpen, setPrintErrorOpen] = useState(false);
  const [apiMessage, setApiMessage] = useState({ open: false, message: '', severity: 'error' });
  const [masterRows, setMasterRows] = useState([]);
  const [masterOptions, setMasterOptions] = useState({ storage_locations: [], operators: [], sticker_refs: [] });
  const [masterLoaded, setMasterLoaded] = useState(false);
  const [optionsLoaded, setOptionsLoaded] = useState(false);
  const [entries, setEntries] = useState([]);
  const [loadingMasters, setLoadingMasters] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [reprinting, setReprinting] = useState(false);
  const [lastPrintPayload, setLastPrintPayload] = useState(null);
  const [backendToken, setBackendToken] = useState('');
  const [authLoading, setAuthLoading] = useState(true);
  const exchangedPortalTokenRef = useRef('');

  // table selection + pagination
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;

  const scope = useMemo(() => ({
    plant_id: plantId,
    factory,

  }), [plantId, factory]);
  const missingScope = useMemo(() => [
    !plantId && 'plant_id',
    !factory && 'factory',

  ].filter(Boolean), [factory, plantId]);

  const showApiMessage = useCallback((message, severity = 'error') => {
    setApiMessage({ open: true, message, severity });
  }, []);

  useEffect(() => {
    let active = true;
    if (isMesAccessToken(authAccessToken)) {
      setBackendToken(authAccessToken);
      setAuthLoading(false);
      return () => { active = false; };
    }
    if (!portalAccessToken || exchangedPortalTokenRef.current === portalAccessToken) {
      return () => { active = false; };
    }

    exchangedPortalTokenRef.current = portalAccessToken;
    setAuthLoading(true);
    exchangeToken({ force: true })
      .then((token) => {
        if (!active) return;
        if (!token) throw new Error('Unable to obtain MES access token.');
        setBackendToken(token);
      })
      .catch(() => {
        if (active) {
          exchangedPortalTokenRef.current = '';
          showApiMessage('ไม่สามารถยืนยันตัวตนกับ MES ได้ กรุณาเข้าสู่ระบบใหม่');
        }
      })
      .finally(() => {
        if (active) setAuthLoading(false);
      });
    return () => { active = false; };
  }, [authAccessToken, exchangeToken, portalAccessToken, showApiMessage]);

  const callWithAuth = useCallback(async (request) => {
    let token = isMesAccessToken(backendToken)
      ? backendToken
      : await exchangeToken({ force: true });
    if (!token) throw new Error('MES access token is unavailable.');
    try {
      return await request(token);
    } catch (error) {
      if (error?.response?.status !== 401) throw error;
      token = await exchangeToken({ force: true });
      if (!token) throw error;
      setBackendToken(token);
      return request(token);
    }
  }, [backendToken, exchangeToken]);

  const getErrorMessage = (error, fallback) => (
    error?.response?.data?.detail
    || error?.response?.data?.message
    || error?.response?.data?.error
    || fallback
  );

  const loadHistory = useCallback(async () => {
    if (!backendToken || !plantId || !factory) return;
    setLoadingHistory(true);
    try {
      const response = await callWithAuth((token) => getWipRegistrations(
        { ...scope, in_stock: true },
        token,
      ));
      setEntries(extractApiList(response.data).map(mapRegistrationToRow));
    } catch (error) {
      showApiMessage(getErrorMessage(error, 'ไม่สามารถโหลดประวัติ WIP ได้'));
    } finally {
      setLoadingHistory(false);
    }
  }, [backendToken, callWithAuth, factory, plantId, scope, showApiMessage]);

  useEffect(() => {
    let active = true;
    if (!backendToken || !plantId || !factory) return undefined;

    const loadOptions = async () => {
      setOptionsLoaded(false);
      try {
        const optionResponse = await callWithAuth((token) => getWipMasterOptions(
          { plant_id: plantId, factory },
          token,
        ));
        if (!active) return;

        const options = extractApiList(optionResponse.data)[0] || {};
        const normalizedOptions = {
          storage_locations: Array.isArray(options.storage_locations) ? options.storage_locations : [],
          operators: Array.isArray(options.operators) ? options.operators : [],
          sticker_refs: Array.isArray(options.sticker_refs) ? options.sticker_refs : [],
        };
        setMasterOptions(normalizedOptions);
        setLocation((current) => current || normalizedOptions.storage_locations[0] || '');
        setOperator((current) => (
          current
          || (normalizedOptions.operators.includes(loggedInName) ? loggedInName : '')
          || normalizedOptions.operators[0]
          || loggedInName
          || ''
        ));
      } catch (error) {
        if (active) showApiMessage(getErrorMessage(error, 'ไม่สามารถโหลด Location, Operate และ Ref. ได้'));
      } finally {
        if (active) setOptionsLoaded(true);
      }
    };

    loadOptions();
    return () => { active = false; };
  }, [backendToken, callWithAuth, factory, loggedInName, plantId, showApiMessage]);

  useEffect(() => {
    let active = true;
    if (!backendToken || !plantId || !factory) return undefined;

    const loadMasters = async () => {
      setLoadingMasters(true);
      setMasterLoaded(false);
      try {
        const masterResponse = await callWithAuth((token) => getWipMaster(scope, token));
        if (!active) return;

        const rows = extractApiList(masterResponse.data);
        setMasterRows(rows);
      } catch (error) {
        if (active) showApiMessage(getErrorMessage(error, 'ไม่สามารถโหลด WIP master ได้'));
      } finally {
        if (active) {
          setLoadingMasters(false);
          setMasterLoaded(true);
        }
      }
    };

    loadMasters();
    return () => { active = false; };
  }, [backendToken, callWithAuth, factory, plantId, scope, showApiMessage]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const productOptions = useMemo(() => {
    const products = new Map();
    masterRows.forEach((row) => {
      const productCode = row.product_code || '';
      const label = row.product_name || '';
      if (!productCode && !label) return;
      const key = `${productCode}|${label}`;
      if (!products.has(key)) products.set(key, { id: key, productCode, label });
    });
    return [...products.values()];
  }, [masterRows]);

  const selectedProductRows = useMemo(() => masterRows.filter((row) => (
    String(row.product_code || '') === String(matCode)
    && row.product_name === productName
  )), [masterRows, matCode, productName]);

  const productTypes = useMemo(
    () => uniqueNonEmpty(selectedProductRows.map((row) => row.product_type)),
    [selectedProductRows],
  );
  const componentOptions = useMemo(
    () => uniqueNonEmpty(selectedProductRows
      .filter((row) => !productType || row.product_type === productType)
      .map((row) => row.ingredient)),
    [productType, selectedProductRows],
  );
  const processSteps = useMemo(
    () => uniqueNonEmpty(selectedProductRows
      .filter((row) => (!productType || row.product_type === productType)
        && (!component || row.ingredient === component))
      .map((row) => row.process_step)),
    [component, productType, selectedProductRows],
  );
  const selectedMaster = useMemo(() => selectedProductRows.find((row) => (
    row.product_type === productType
    && row.ingredient === component
    && row.process_step === processStep
  )), [component, processStep, productType, selectedProductRows]);

  useEffect(() => {
    if (!selectedMaster) {
      setEndDate(startDate);
      return;
    }
    setEndDate(addWipDuration(startDate, selectedMaster.time_step, selectedMaster.time_step_unit));
  }, [selectedMaster, startDate]);

  useEffect(() => {
    setWeight(selectedMaster?.weight ? '' : 0);
    setQty(selectedMaster?.qty ? '' : 0);
  }, [selectedMaster?.id, selectedMaster?.qty, selectedMaster?.weight]);

  const selectProduct = (value) => {
    if (!value) {
      setProductName('');
      setMatCode('');
      setProductType('');
      setComponent('');
      setProcessStep('');
      return;
    }
    const rows = masterRows.filter((row) => (
      String(row.product_code || '') === String(value.productCode)
      && row.product_name === value.label
    ));
    const first = rows[0] || {};
    setProductName(value.label);
    setMatCode(value.productCode);
    setProductType(first.product_type || '');
    setComponent(first.ingredient || '');
    setProcessStep(first.process_step || '');
  };

  const changeProductType = (value) => {
    const first = selectedProductRows.find((row) => row.product_type === value) || {};
    setProductType(value);
    setComponent(first.ingredient || '');
    setProcessStep(first.process_step || '');
  };

  const changeComponent = (value) => {
    const first = selectedProductRows.find((row) => (
      row.product_type === productType && row.ingredient === value
    )) || {};
    setComponent(value);
    setProcessStep(first.process_step || '');
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = filteredEntries.map((n) => n.id);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClickRow = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }

    setSelected(newSelected);
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const filteredEntries = entries.filter((row) => {
    const byName = (row.productName || '').toLowerCase().includes(searchText.toLowerCase());
    return byName;
  });

  const totalPages = Math.max(1, Math.ceil(filteredEntries.length / rowsPerPage));
  const startItem = filteredEntries.length === 0 ? 0 : page * rowsPerPage + 1;
  const endItem = Math.min(filteredEntries.length, (page + 1) * rowsPerPage);

  useEffect(() => {
    setPage(0);
  }, [searchText]);

  useEffect(() => {
    if (page >= totalPages) setPage(totalPages - 1);
  }, [page, totalPages]);

  const buildHistoryPrintPayload = useCallback((row) => {
    const registeredAt = new Date(row.date);
    const printDate = Number.isNaN(registeredAt.getTime()) ? new Date() : registeredAt;
    const matchingMaster = masterRows.find((master) => (
      String(master.product_code || '') === String(row.matCode || '')
      && master.product_name === row.productName
      && master.product_type === row.type
      && master.ingredient === row.component
      && master.process_step === row.processStep
    ));
    const startAt = dateAtTime(printDate, row.start);
    const endAt = dateAtTime(printDate, row.end);
    const useWithinDate = addWipDuration(
      endAt || printDate,
      matchingMaster?.shelf_life,
      matchingMaster?.shelf_life_unit,
    ) || endAt || printDate;

    return {
      date: row.date,
      matCode: row.matCode,
      productName: row.productName,
      type: row.type,
      start: row.start,
      end: row.end,
      lot: row.lot,
      batch: row.batch,
      shift: row.shift,
      weight: row.weight,
      qty: row.qty,
      unit: row.unit,
      qtyUnit: row.unit,
      location: row.location,
      component: row.component,
      processStep: row.processStep,
      ref: row.ref,
      no: row.no,
      stickerDate: formatStickerDate(printDate),
      productionDate: formatLocalDate(printDate),
      useWithin: formatLocalDateTime(useWithinDate),
      delayTime: calculateDelayMinutes(startAt, endAt),
      operator: row.createdBy,
    };
  }, [masterRows]);

  const printPayload = useCallback(async (payload) => {
    setLastPrintPayload(payload);
    await printWipSticker(payload);
  }, []);

  const handleReprintSelected = async () => {
    if (selected.length !== 1 || reprinting) return;
    const row = entries.find((entry) => entry.id === selected[0]);
    if (!row) return;
    setReprinting(true);
    try {
      await printPayload(buildHistoryPrintPayload(row));
      showApiMessage('เปิดคำสั่ง Reprint แล้ว', 'success');
    } catch (error) {
      console.error('Unable to reprint WIP sticker.', error);
      setPrintErrorOpen(true);
    } finally {
      setReprinting(false);
    }
  };

  const handleRetryLastPrint = async () => {
    if (!lastPrintPayload || reprinting) return;
    setReprinting(true);
    try {
      await printWipSticker(lastPrintPayload);
      setPrintErrorOpen(false);
    } catch (error) {
      console.error('Unable to retry WIP sticker print.', error);
    } finally {
      setReprinting(false);
    }
  };

  const handleDeleteSelected = async () => {
    if (selected.length === 0 || deleting) return;
    setDeleting(true);
    try {
      await callWithAuth((token) => Promise.all(
        selected.map((id) => deleteWipRegistration(id, token)),
      ));
      setEntries((current) => current.filter((row) => !selected.includes(row.id)));
      setSelected([]);
      setDeleteConfirmOpen(false);
      showApiMessage('ลบข้อมูลเรียบร้อยแล้ว', 'success');
    } catch (error) {
      showApiMessage(getErrorMessage(error, 'ไม่สามารถลบข้อมูล WIP ได้'));
    } finally {
      setDeleting(false);
    }
  };

  const handleSave = async () => {
    if (saving) return;
    if (!backendToken || !plantId || !factory) {
      showApiMessage('ไม่พบ plant หรือ factory ของผู้ใช้งาน');
      return;
    }
    if (!location || !selectedMaster || !lot.trim() || !batch.trim() || !shift || !operator) {
      showApiMessage('กรุณากรอกข้อมูลที่จำเป็นให้ครบ');
      return;
    }
    if (selectedMaster.weight && (!/^\d{1,3}(\.\d{1,2})?$/.test(String(weight)))) {
      showApiMessage('กรุณากรอก Weight ให้ถูกต้อง');
      return;
    }
    if (selectedMaster.qty && (qty === '' || Number(qty) < 0 || Number(qty) > 9999 || Number.isNaN(Number(qty)))) {
      showApiMessage('กรุณากรอก Qty ระหว่าง 0-9999');
      return;
    }
    if (refValue && !noValue.trim()) {
      showApiMessage('กรุณากรอก No. สำหรับ Ref. ที่เลือก');
      return;
    }

    if (noValue && !/^\d{1,3}$/.test(noValue)) { showApiMessage('No. ต้องเป็นตัวเลขไม่เกิน 3 หลัก'); return; }
    const now = new Date();
    const unit = selectedMaster.storage_unit || '';
    const apiPayload = {
      plant_id: plantId,
      factory,

      location,
      mat_code: matCode,
      product_name: productName,
      type: productType,
      components: component,
      process_step: processStep,
      start_time: formatApiTime(startDate),
      end_time: formatApiTime(endDate),
      lot,
      batch,
      shift,
      weight: selectedMaster.weight ? Number(weight) : 0,
      qty: selectedMaster.qty ? Number(qty) : 0,
      unit,
      ref: refValue,
      no: noValue,
      created_by: operator,
    };

    setSaving(true);
    try {
      const response = await callWithAuth((token) => createWipRegistration(apiPayload, token));
      const created = response.data?.data || response.data;
      if (created?.id) {
        const newRow = mapRegistrationToRow(created);
        setEntries((current) => [newRow, ...current.filter((row) => row.id !== newRow.id)]);
      } else {
        await loadHistory();
      }
      setSaveToastOpen(true);

      const useWithinDate = addWipDuration(
        endDate,
        selectedMaster.shelf_life,
        selectedMaster.shelf_life_unit,
      ) || endDate;
      const printPayload = {
        id: created?.id || '',
        date: created?.created_at || now.toISOString(),
        matCode: created?.mat_code || matCode,
        productName: created?.product_name || productName,
        type: created?.type || productType,
        start: created?.start_time || formatApiTime(startDate),
        end: created?.end_time || formatApiTime(endDate),
        lot: created?.lot || lot,
        batch: created?.batch || batch,
        shift: created?.shift || shift,
        weight: created?.weight ?? apiPayload.weight,
        qty: created?.qty ?? apiPayload.qty,
        unit: created?.unit || unit,
        qtyUnit: created?.unit || unit,
        location: created?.location || location,
        component: created?.components || component,
        processStep: created?.process_step || processStep,
        ref: created?.ref || refValue,
        no: created?.no || noValue,
        stickerDate: formatStickerDate(now),
        productionDate: formatLocalDate(now),
        useWithin: formatLocalDateTime(useWithinDate),
        delayTime: calculateDelayMinutes(startDate, endDate),
        operator: created?.created_by || operator,
      };

      try {
        await printWipSticker(printPayload);
        setLastPrintPayload(printPayload);
      } catch (printError) {
        setLastPrintPayload(printPayload);
        console.error('Unable to auto-print WIP sticker.', printError);
        setPrintErrorOpen(true);
      }

      setLot('');
      setBatch('');
      setWeight(selectedMaster.weight ? '' : 0);
      setQty(selectedMaster.qty ? '' : 0);
      setRefValue('');
      setNoValue('');
    } catch (error) {
      showApiMessage(getErrorMessage(error, 'ไม่สามารถบันทึกข้อมูล WIP ได้'));
    } finally {
      setSaving(false);
    }
  };

  const controlSx = {
    '& .MuiInputBase-root': {
      height: 56,
      borderRadius: 1.2,
      bgcolor: '#fff',
    },
    '& .MuiInputBase-input': {
      py: 1.05,
      fontSize: 20,
    },
  };

  return (
    <div>
      <Helmet>
        <title>Register | MES</title>
      </Helmet>
      <Container maxWidth={false} sx={{ mt: 2, mb: 4, maxWidth: '1440px', px: { xs: 1.5, md: 3 } }}>
        <MonitorNavigation />
        {!authLoading && missingScope.length > 0 && (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            ไม่พบ scope: {missingScope.join(', ')} — กรุณาเปิด WIP Tracking จาก Plant / Factory ที่ต้องการ
          </Alert>
        )}
        {optionsLoaded && plantId && factory && masterOptions.storage_locations.length === 0 && (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            ไม่พบ WIP master-options สำหรับ plant_id={plantId}, factory={factory}
          </Alert>
        )}
        {masterLoaded && missingScope.length === 0 && masterRows.length === 0 && (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            ไม่พบ WIP master สำหรับ plant_id={plantId}, factory={factory}
          </Alert>
        )}
        <Box sx={{ bgcolor: '#def6f3', p: { xs: 1.2, md: 2 }, borderRadius: 2 }}>
          <Box sx={{ bgcolor: '#fff', borderRadius: 2, px: { xs: 1.5, md: 2.5 }, py: 2, mb: 2 }}>
            <Grid container spacing={1.6}>
              <Grid item xs={12}>
                <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: 28, md: 34 }, lineHeight: 1.15, mb: 1 }}>เลือกสถานที่ (Location)</Typography>
              </Grid>

              <Grid item xs={12} md={7}>
                <LocationSelect
                  location={location}
                  setLocation={setLocation}
                  locations={masterOptions.storage_locations}
                  disabled={authLoading || !optionsLoaded || saving}
                />
              </Grid>
              <Grid item xs={12} md={5} sx={{ borderLeft: { xs: 'none', md: '1px solid #d7dfdf' }, pl: { xs: 0, md: 1.4 } }}>
                <Box sx={{ bgcolor: '#eaf8f6', px: 2, py: 1.25, borderRadius: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, minHeight: 72 }}>
                  <Box sx={{ width: 52, height: 52, borderRadius: '50%', bgcolor: '#07bcc8', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <WarehouseOutlinedIcon sx={{ color: '#fff', fontSize: 28 }} />
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#00B3BD', display: 'block', lineHeight: 1.1, fontSize: 20 }}>Current Location</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.15, fontSize: { xs: 23, md: 28 }, mt: 0.2 }}>{location || '-'}</Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Grid container spacing={2} alignItems="stretch">

          
          <Grid item xs={12} md={7} sx={{ display: 'flex' }}>
            <ProductIdentification
              productName={productName}
              matCode={matCode}
              productOptions={productOptions}
              onProductChange={selectProduct}
              productType={productType}
              setProductType={changeProductType}
              productTypes={productTypes}
              component={component}
              setComponent={changeComponent}
              components={componentOptions}
              disabled={authLoading || loadingMasters || saving}
            />
          </Grid>

          <Grid item xs={12} md={5} sx={{ display: 'flex' }}>
            <WIPDetail
              processStep={processStep}
              setProcessStep={setProcessStep}
              processSteps={processSteps}
              startDate={startDate}
              setStartDate={setStartDate}
              endDate={endDate}
              disabled={authLoading || loadingMasters || saving || !selectedMaster}
            />
          </Grid>

          <Grid item xs={12}>
            <ProductionInputs
              lot={lot}
              setLot={setLot}
              batch={batch}
              setBatch={setBatch}
              shift={shift}
              setShift={setShift}
              weight={weight}
              setWeight={setWeight}
              weightRequired={Boolean(selectedMaster?.weight)}
              qty={qty}
              setQty={setQty}
              qtyRequired={Boolean(selectedMaster?.qty)}
              unit={selectedMaster?.storage_unit || ''}
              refValue={refValue}
              setRefValue={setRefValue}
              noValue={noValue}
              setNoValue={setNoValue}
              refOptions={masterOptions.sticker_refs}
              operator={operator}
              setOperator={setOperator}
              operatorOptions={masterOptions.operators}
              disabled={authLoading || loadingMasters || saving || !selectedMaster}
            />
          </Grid>

          <Grid item xs={12}>
            <SaveBar onSave={handleSave} saving={saving} disabled={authLoading || loadingMasters || !selectedMaster} />
          </Grid>

          <Grid item xs={12}>
            <Card sx={{ p: 1.5, mt: 0.7, borderRadius: 1.5, boxShadow: 'none', border: '1px solid #ecf0f0' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.85, mb: 1.1 }}>
                <AssignmentOutlinedIcon fontSize="small" sx={{ color: '#00B3BD' }} />
                <Typography variant="subtitle1" sx={{ color: '#00B3BD', fontSize: 22, fontWeight: 500 }}>Registration History</Typography>
              </Box>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: 'minmax(280px, 0.95fr) minmax(520px, 2fr) auto' },
                  alignItems: 'center',
                  columnGap: 1.2,
                  rowGap: 1,
                  mb: 0.85,
                }}
              >
                <TextField
                  placeholder=""
                  value={''}
                  size="small"
                  sx={{ width: '100%', ...controlSx }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <CalendarTodayOutlinedIcon fontSize="small" sx={{ color: '#2a2a2a', fontSize: 28 }} />
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  placeholder="ค้นหาชื่อสินค้า"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  size="small"
                  sx={{ width: '100%', ...controlSx }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" sx={{ fontSize: 29 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setSearchText('')}><CloseIcon fontSize="small" sx={{ fontSize: 28 }} /></IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Box sx={{ justifySelf: { xs: 'stretch', md: 'end' }, mt: { xs: 0, md: 0.55 } }}>
                  <ExportExcel
                    excelData={filteredEntries}
                    fileName={`WIP_Register_History_${plantId}_${factory}_${formatLocalDate(new Date()).replace(/-/g, '')}`}
                  />
                </Box>
              </Box>

              <Box sx={{ bgcolor: '#ffecef', p: 0.72, borderRadius: 0.8, display: 'flex', alignItems: 'center', mb: 0.75 }}>
                <Button
                  variant="contained"
                  startIcon={<PrintOutlinedIcon />}
                  disabled={selected.length !== 1 || reprinting}
                  sx={{ mr: 1, minWidth: 110, borderRadius: 1, py: 0.62, px: 1.25, fontSize: 16, boxShadow: 'none', bgcolor: '#00B3BD', '&:hover': { bgcolor: '#009da6' } }}
                  onClick={handleReprintSelected}
                >
                  {reprinting ? 'PRINTING...' : 'REPRINT'}
                </Button>
                <Button variant="contained" color="error" disabled={selected.length === 0 || deleting} sx={{ mr: 1, minWidth: 92, borderRadius: 1, py: 0.62, px: 1.25, fontSize: 16, boxShadow: 'none' }} onClick={() => setDeleteConfirmOpen(true)}>{deleting ? 'DELETING...' : 'DELETE'}</Button>
                <Typography variant="body2" sx={{ color: '#ef3d3d', fontWeight: 500, fontSize: 16 }}>{selected.length} ROW(S) SELECTED</Typography>
              </Box>
              <TableContainer component={Paper} sx={{ borderRadius: 1.1, boxShadow: 'none', border: '1px solid #e1e1e1' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ '& .MuiTableCell-root': { verticalAlign: 'middle', height: 52, boxSizing: 'border-box' } }}>
                        <TableCell padding="checkbox" sx={{ bgcolor: '#00bfa5', py: 0.82 }}>
                          <Checkbox
                            sx={{ color: '#fff', '&.Mui-checked': { color: '#fff' } }}
                            checked={filteredEntries.length > 0 && selected.length === filteredEntries.length}
                            indeterminate={selected.length > 0 && selected.length < filteredEntries.length}
                            onChange={handleSelectAllClick}
                          />
                        </TableCell>
                        <TableCell sx={{ bgcolor: '#00bfa5', color: '#fff', py: 0.82, px: 0.95 }}>
                          <Typography sx={{ fontSize: 16, fontWeight: 700, lineHeight: 1 }}>No.</Typography>
                        </TableCell>
                      <TableCell sx={{ bgcolor: '#00bfa5', color: '#fff', py: 0.82, px: 0.95 }}>
                        <Typography sx={{ fontSize: 16, fontWeight: 700, lineHeight: 1 }}>Date</Typography>
                      </TableCell>
                      <TableCell sx={{ bgcolor: '#00bfa5', color: '#fff', py: 0.82, px: 0.95 }}>
                        <Typography sx={{ fontSize: 16, fontWeight: 700, lineHeight: 1 }}>Mat. Code</Typography>
                      </TableCell>
                      <TableCell sx={{ bgcolor: '#00bfa5', color: '#fff', py: 0.82, px: 0.95 }}>
                        <Typography sx={{ fontSize: 16, fontWeight: 700, lineHeight: 1 }}>Product Name</Typography>
                      </TableCell>
                      <TableCell sx={{ bgcolor: '#00bfa5', color: '#fff', py: 0.82, px: 0.95 }}>
                        <Typography sx={{ fontSize: 16, fontWeight: 700, lineHeight: 1 }}>Type</Typography>
                      </TableCell>
                      <TableCell sx={{ bgcolor: '#00bfa5', color: '#fff', py: 0.82, px: 0.95 }}>
                        <Typography sx={{ fontSize: 16, fontWeight: 700, lineHeight: 1 }}>Start</Typography>
                      </TableCell>
                      <TableCell sx={{ bgcolor: '#00bfa5', color: '#fff', py: 0.82, px: 0.95 }}>
                        <Typography sx={{ fontSize: 16, fontWeight: 700, lineHeight: 1 }}>End</Typography>
                      </TableCell>
                      <TableCell sx={{ bgcolor: '#00bfa5', color: '#fff', py: 0.82, px: 0.95 }}>
                        <Typography sx={{ fontSize: 16, fontWeight: 700, lineHeight: 1 }}>Lot</Typography>
                      </TableCell>
                      <TableCell sx={{ bgcolor: '#00bfa5', color: '#fff', py: 0.82, px: 0.95 }}>
                        <Typography sx={{ fontSize: 16, fontWeight: 700, lineHeight: 1 }}>Batch</Typography>
                      </TableCell>
                      <TableCell sx={{ bgcolor: '#00bfa5', color: '#fff', py: 0.82, px: 0.95 }}>
                        <Typography sx={{ fontSize: 16, fontWeight: 700, lineHeight: 1 }}>Shift</Typography>
                      </TableCell>
                      <TableCell sx={{ bgcolor: '#00bfa5', color: '#fff', py: 0.82, px: 0.95 }}>
                        <Typography sx={{ fontSize: 16, fontWeight: 700, lineHeight: 1 }}>Weight(kg)</Typography>
                      </TableCell>
                      <TableCell sx={{ bgcolor: '#00bfa5', color: '#fff', py: 0.82, px: 0.95 }}>
                        <Typography sx={{ fontSize: 16, fontWeight: 700, lineHeight: 1 }}>Qty</Typography>
                      </TableCell>
                      <TableCell sx={{ bgcolor: '#00bfa5', color: '#fff', py: 0.82, px: 0.95 }}>
                        <Typography sx={{ fontSize: 16, fontWeight: 700, lineHeight: 1 }}>Unit</Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loadingHistory && (
                      <TableRow><TableCell colSpan={14} align="center">กำลังโหลดข้อมูล...</TableCell></TableRow>
                    )}
                    {filteredEntries.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => {
                      const isItemSelected = isSelected(row.id);
                      return (
                        <TableRow key={row.id} hover role="checkbox" aria-checked={isItemSelected} selected={isItemSelected} onClick={(event) => handleClickRow(event, row.id)}>
                          <TableCell padding="checkbox" sx={{ py: 0.75, px: 0.6 }}>
                            <Checkbox color="primary" checked={isItemSelected} />
                          </TableCell>
                          <TableCell sx={{ py: 0.75, px: 0.95, fontSize: 20 }}>{row.id}</TableCell>
                          <TableCell sx={{ py: 0.75, px: 0.95, fontSize: 20 }}>{row.date}</TableCell>
                          <TableCell sx={{ py: 0.75, px: 0.95, fontSize: 20 }}>{row.matCode}</TableCell>
                          <TableCell sx={{ py: 0.75, px: 0.95, fontSize: 20 }}>{row.productName}</TableCell>
                          <TableCell sx={{ py: 0.75, px: 0.95 }}>
                            <Box sx={{ display: 'inline-flex', px: 1.2, py: 0.25, borderRadius: 1.6, bgcolor: '#e2f8df', color: '#47a057', fontSize: 20, lineHeight: 1.2 }}>
                              {row.type}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ py: 0.75, px: 0.95, fontSize: 20 }}>{row.start}</TableCell>
                          <TableCell sx={{ py: 0.75, px: 0.95, fontSize: 20 }}>{row.end}</TableCell>
                          <TableCell sx={{ py: 0.75, px: 0.95, fontSize: 20 }}>{row.lot}</TableCell>
                          <TableCell sx={{ py: 0.75, px: 0.95, fontSize: 20 }}>{row.batch}</TableCell>
                          <TableCell sx={{ py: 0.75, px: 0.95 }}>
                            <Box sx={{ display: 'inline-flex', px: 1.2, py: 0.2, borderRadius: 1.6, bgcolor: '#e2f8df', color: '#47a057', fontSize: 20, lineHeight: 1.2 }}>
                              {row.shift}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ py: 0.75, px: 0.95, fontSize: 20 }}>{row.weight}</TableCell>
                          <TableCell sx={{ py: 0.75, px: 0.95, fontSize: 20 }}>{row.qty}</TableCell>
                          <TableCell sx={{ py: 0.75, px: 0.95, fontSize: 20 }}>{row.unit}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.7, alignItems: 'center' }}>
                <Typography variant="body2" sx={{ fontSize: 20 }}>แสดง {startItem} ถึง {endItem} จาก {filteredEntries.length} รายการ</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.85 }}>
                  <IconButton
                    size="small"
                    onClick={() => handleChangePage(null, Math.max(0, page - 1))}
                    sx={{ border: '1px solid #ddd', borderRadius: 0.8, bgcolor: '#fff', p: 0.4 }}
                  >
                    <NavigateBeforeIcon sx={{ fontSize: 22 }} />
                  </IconButton>
                  <Box sx={{ minWidth: 36, height: 36, px: 0.9, borderRadius: 0.9, bgcolor: '#00B3BD', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>
                    {page + 1}
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => handleChangePage(null, Math.min(totalPages - 1, page + 1))}
                    sx={{ border: '1px solid #ddd', borderRadius: 0.8, bgcolor: '#fff', p: 0.4 }}
                  >
                    <NavigateNextIcon sx={{ fontSize: 22 }} />
                  </IconButton>
                </Box>
              </Box>
            </Card>
          </Grid>
        </Grid>
        </Box>
        <Dialog open={deleteConfirmOpen} onClose={() => !deleting && setDeleteConfirmOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>ยืนยันการลบข้อมูล</DialogTitle>
          <DialogContent>
            <Typography>
              ต้องการลบข้อมูล WIP ที่เลือกจำนวน {selected.length} รายการใช่หรือไม่? เมื่อลบแล้วจะไม่สามารถ Reprint จากรายการนี้ได้
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteConfirmOpen(false)} disabled={deleting}>ยกเลิก</Button>
            <Button variant="contained" color="error" onClick={handleDeleteSelected} disabled={deleting}>
              {deleting ? 'กำลังลบ...' : 'ยืนยันลบ'}
            </Button>
          </DialogActions>
        </Dialog>
        <Snackbar
          open={saveToastOpen}
          autoHideDuration={2500}
          onClose={() => setSaveToastOpen(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSaveToastOpen(false)}
            severity="success"
            variant="filled"
            sx={{
              bgcolor: '#dff3e5',
              color: '#2f7f45',
              '& .MuiAlert-icon': { color: '#2f9e44' },
              '& .MuiAlert-action': { color: '#667' },
              border: '1px solid #68c47e',
              borderRadius: 1.5,
              minWidth: 340,
              '& .MuiAlert-message': { fontSize: 20, fontWeight: 500 },
            }}
          >
            บันทึกข้อมูลเรียบร้อยแล้ว
          </Alert>
        </Snackbar>
        <Snackbar
          open={printErrorOpen}
          autoHideDuration={5000}
          onClose={() => setPrintErrorOpen(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert
            severity="warning"
            onClose={() => setPrintErrorOpen(false)}
            action={(
              <Button color="inherit" size="small" disabled={!lastPrintPayload || reprinting} onClick={handleRetryLastPrint}>
                REPRINT
              </Button>
            )}
            sx={{ width: '100%' }}
          >
            บันทึกสำเร็จ แต่ไม่สามารถสั่งพิมพ์ Sticker ได้ กรุณาตรวจสอบเครื่องพิมพ์
          </Alert>
        </Snackbar>
        <Snackbar
          open={apiMessage.open}
          autoHideDuration={5000}
          onClose={() => setApiMessage((current) => ({ ...current, open: false }))}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert
            severity={apiMessage.severity}
            onClose={() => setApiMessage((current) => ({ ...current, open: false }))}
            sx={{ width: '100%' }}
          >
            {apiMessage.message}
          </Alert>
        </Snackbar>
      </Container>
    </div>
  );
}

